import axios from "axios";

// Same-origin in production (keepsoul.app, dream-app-17.emergent.host)
// Cross-origin only during preview development.
const ENV_URL = process.env.REACT_APP_BACKEND_URL;
const PROD_HOSTS = ["keepsoul.app", "www.keepsoul.app", "dream-app-17.emergent.host"];
const inProd = typeof window !== "undefined" && PROD_HOSTS.includes(window.location.hostname);
const BACKEND_URL = inProd ? window.location.origin : ENV_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  // Backend cold-starts on first request after a redeploy can take a few
  // seconds while the Atlas connection pool warms up. We give every call
  // a generous 25s ceiling and rely on the retry interceptor below to
  // smooth over transient network blips.
  timeout: 25000,
});

// Auto-retry once on transient network / 5xx / 429 errors. Most cold-start
// "Ha ocurrido un error" reports were caused by the very first request after
// a redeploy timing out before Mongo Atlas finished establishing its pool,
// and rate-limit (429) hits when many widgets fan out on page load.
api.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    const cfg = err?.config || {};
    const isNetwork = !err.response;
    const status = err.response?.status;
    const is5xx = status && status >= 500;
    const is429 = status === 429;
    const isIdempotentMethod = ["get", "head", "options"].includes((cfg.method || "get").toLowerCase());
    const retryablePost = cfg.url && (
      cfg.url.includes("/auth/login") ||
      cfg.url.includes("/auth/register") ||
      cfg.url.includes("/auth/me") ||
      cfg.url.includes("/auth/lang")
    );
    const retriesLeft = cfg.__retriesLeft ?? (is429 ? 2 : 1);

    if (retriesLeft > 0 && (isNetwork || is5xx || is429) && (isIdempotentMethod || retryablePost)) {
      cfg.__retriesLeft = retriesLeft - 1;
      // Honor the server's Retry-After header for 429 (in seconds), otherwise
      // use a small jitter. Cap to 4s so a slow page doesn't feel frozen.
      let waitMs = 350;
      if (is429) {
        const retryAfter = parseInt(err.response?.headers?.["retry-after"] || "", 10);
        waitMs = Number.isFinite(retryAfter) ? Math.min(retryAfter * 1000, 4000) : 1200;
      }
      await new Promise((r) => setTimeout(r, waitMs));
      try {
        return await api.request(cfg);
      } catch (_) { /* fall through */ }
    }
    // Mark 429 errors as already-handled so React's dev runtime-error
    // overlay doesn't pop up. The caller still gets the rejection and can
    // toast / log it appropriately, but we never crash the page.
    if (is429) {
      err.__handled = true;
    }
    return Promise.reject(err);
  }
);

// Last-resort safety net: swallow uncaught axios rejections that already
// surfaced their error to the user (or are non-blocking polls). This stops
// the giant red React error overlay in dev while still allowing component
// code that `try { ... } catch` to handle the response.
if (typeof window !== "undefined") {
  const swallow = (event) => {
    const reason = event.reason || {};
    if (reason.__handled || reason.isAxiosError) {
      event.preventDefault();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    }
  };
  window.addEventListener("unhandledrejection", swallow, true);

  // Cross-origin "Script error." from Turnstile / Stripe / Posthog. These are
  // opaque, non-actionable, and would otherwise trigger the dev-server
  // overlay covering the whole UI. Intercept in capture phase.
  window.addEventListener("error", (event) => {
    if (event && (event.message === "Script error." || !event.message)) {
      event.preventDefault();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      return false;
    }
    return undefined;
  }, true);

  // Dev-only: if webpack-dev-server / react-refresh inject their error
  // overlay iframe, remove it immediately. Production builds never inject it.
  const isDev = process.env.NODE_ENV !== "production";
  if (isDev && document.body) {
    const removeOverlays = () => {
      const overlay = document.querySelector(
        'iframe#webpack-dev-server-client-overlay, iframe[name="webpack-dev-server-client-overlay"]'
      );
      if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
    };
    const observer = new MutationObserver(removeOverlays);
    observer.observe(document.body, { childList: true, subtree: false });
    // Initial pass in case it already exists
    setTimeout(removeOverlays, 0);
  }
}

// Suppress dev-overlay noise for the well-known 403 EMAIL_NOT_VERIFIED state.
// The dashboard already renders a friendly "please verify your email" banner;
// individual fetch calls failing for this reason are expected and should not
// surface as unhandled axios rejections in CRA's red dev overlay.
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const data = err?.response?.data;
    const code = (data && (data.code || data?.detail?.code)) || "";
    if (err?.response?.status === 403 && code === "EMAIL_NOT_VERIFIED") {
      // Tag the error so callers can choose to silently ignore it.
      err.silent = true;
    }
    // Plan-limit interceptor: emit a global event so PlanLimitProvider can
    // render the upgrade modal regardless of which screen triggered it.
    if (err?.response?.status === 402 && code === "PLAN_LIMIT_EXCEEDED") {
      const detail = data?.detail || data;
      try {
        window.dispatchEvent(new CustomEvent("ks:plan-limit", { detail }));
      } catch (_) { /* SSR safety */ }
      err.__handled = true;
    }
    // Paywall interceptor: free users hitting a paid endpoint get 403 with
    // detail.code === "plan_required". Emit a global event so PaywallProvider
    // pops the "Unlock KeepSoul" modal.
    if (err?.response?.status === 403 && code === "plan_required") {
      try {
        window.dispatchEvent(new CustomEvent("ks:paywall", { detail: data?.detail || data }));
      } catch (_) { /* SSR safety */ }
      err.__handled = true;
    }
    return Promise.reject(err);
  }
);
